import { Inline } from "PFComponents/containers";
import { Icon } from "PFComponents/icon";
import { Modal } from "PFComponents/modal";
import { getProfileName } from "PFCore/helpers/profile";
import { useBookingTemplate } from "PFCore/hooks/queries/bookings/booking_templates/use_booking_template";
import { Booking, BookingTemplate } from "PFTypes";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { getDaysDifference } from "../../parts/overview/calendar/calendar.utils";
import { BookingFormValues } from "../booking_form/booking_form_provider";
import { BookingType } from "../booking_form/booking_type";
import { GroupBookingsQuestions } from "./group_bookings_questions";
import { GroupBookingsQuestionsMode } from "./group_bookings_questions.types";
import { SpecificTimeBookingsQuestions } from "./specific_time_bookings_questions";

const checkIsSingleDayBookingTemplate = (bookingTemplate?: BookingTemplate) =>
  bookingTemplate &&
  getDaysDifference(new Date(bookingTemplate.start_date), new Date(bookingTemplate.end_date)) <= 0;

type UpdateBookingModalProps = {
  onClose: () => void;
  onConfirm: () => Promise<void>;
  initialBooking?: Booking;
};

export const UpdateQuestionsModal = ({ onClose, onConfirm, initialBooking }: UpdateBookingModalProps) => {
  const { t } = useTranslation(["bookingModule", "translation"]);
  const { control } = useFormContext<BookingFormValues>();

  const { data: bookingTemplate } = useBookingTemplate(initialBooking?.booking_template_id, {
    enabled: !!initialBooking?.booking_template_id
  });

  const [watchBookingType, watchWorkforceMember] = useWatch<BookingFormValues>({
    name: ["bookingType", "workforceMember"]
  });

  const isRepeated = watchBookingType === BookingType.Repeated;
  const isGrouped = !!initialBooking?.booking_group_id;
  const isSingleDayBookingTemplate = checkIsSingleDayBookingTemplate(bookingTemplate);
  const profileFullName = watchWorkforceMember ? getProfileName(watchWorkforceMember) : "";

  return (
    <Modal
      title={
        <Inline alignItems="center" gap="spacingMd" justifyContent="center">
          <Icon name="warning" color="paletteOrange0" size="lg" />
          {t("bookingModule:bookings.update.editBooking")}
        </Inline>
      }
      labelOK={t("translation:continueText")}
      onOK={onConfirm}
      onClose={onClose}
    >
      <div>
        {isGrouped && (
          <Controller
            name="updateGroupOptionSelected"
            control={control}
            render={({ field }) => (
              <GroupBookingsQuestions
                mode={GroupBookingsQuestionsMode.Update}
                bookingGroupId={initialBooking.booking_group_id!}
                value={field.value}
                onChange={field.onChange}
                userName={profileFullName}
              />
            )}
          />
        )}
        {isRepeated && !isSingleDayBookingTemplate && initialBooking && (
          <Controller
            name="updateRepeatOptionSelected"
            control={control}
            render={({ field }) => (
              <SpecificTimeBookingsQuestions
                label={t("bookingModule:bookings.update.body.question")}
                isEditMode
                value={field.value}
                onChange={field.onChange}
                booking={initialBooking}
              />
            )}
          />
        )}
      </div>
    </Modal>
  );
};
